/* eslint-disable no-plusplus */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Input, Row } from 'reactstrap';

import SearchInput from 'components/layout/forms/SearchInput';
import BasicTable from 'components/layout/tables/BasicTable';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { formatServerDate } from 'services/dates/serverTime';
import { withContentWidths } from 'services/tables/getColumnWidths';
import { WRITE_CONTACTS } from 'services/enums/users/permissions';
import { CREDIT_RATING, useChoiceList } from 'services/offers/offerChoices';
import useHasPermission from 'services/users/useHasPermission';
import useContactTypeOptions from 'services/contacts/useContactTypeOptions';
import { addMessage } from 'store/messages/actions';
import customerApi, { IContactColumnFilters } from '../../api/contacts';
import ContactPersonsTab from './ContactPersonsTab';
import translations from '../../services/translations/translations';
import '../../style/contacts/Contacts.css';
import '../../style/layout/Table.css';

// The searches of the columns are sent when the user stops typing
const FILTER_DELAY = 400;
// The tab chosen last
const TAB_KEY = 'contacts-tab';

const Contacts = (): JSX.Element | null => {
  const { t } = useTranslation([translations.contacts, translations.common]);
  const [contactData, setContactData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [totalSize, setTotalSize] = useState(100);
  const [offset, setOffset] = useState(0);
  const [contactTypes, setContactTypes] = useState<string[]>([]);
  const [prevSelectedType, setPrevSelectedType] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedRow, setSelectedRow] = useState<number | undefined>(undefined);
  const [prevSearchTerm, setPrevSearchTerm] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  // The searches of the columns as typed, and as sent after the user stopped typing
  const [columnFilters, setColumnFilters] = useState<IContactColumnFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<IContactColumnFilters>(
    {}
  );
  const history = useHistory();
  // The companies or the persons of all of them; remembered on this computer
  const [tab, setTab] = useState<'companies' | 'persons'>(() => {
    try {
      return localStorage.getItem(TAB_KEY) === 'persons'
        ? 'persons'
        : 'companies';
    } catch (error) {
      return 'companies';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(TAB_KEY, tab);
    } catch (error) {
      // Not remembered
    }
  }, [tab]);
  // With only the right to read, no contacts are added
  const canWrite = useHasPermission(WRITE_CONTACTS);
  // The types of the filter as the admins have listed them; a chosen hidden type is kept
  const typeOptions = useContactTypeOptions(
    contactTypes,
    selectedType ? [selectedType] : []
  );
  const creditRatings = useChoiceList(
    CREDIT_RATING,
    columnFilters.creditRating
  );

  const dispatch = useDispatch();
  const [exporting, setExporting] = useState(false);

  // All the contacts of the list with its searches and type as an Excel file: the companies
  // and their persons on sheets of their own
  const exportExcel = async () => {
    setExporting(true);
    try {
      const response = await customerApi.export({
        filters: appliedFilters,
        search: searchTerm,
        texts: {
          companiesSheet: t('Contact Info'),
          companyColumns: [
            t('Company'),
            t('Business Id'),
            t('Street Address'),
            t('Postal Number'),
            t('Place'),
            t('Country'),
            t('Contact Types'),
            t('Person count'),
          ],
          fileName: t('export.File name'),
          personColumns: [
            t('Last Name'),
            t('First Name'),
            t('Title'),
            t('Job Description'),
            t('Office'),
            t('Operating area'),
            t(`${translations.common}:Email`),
            t('Phone'),
            t('Company'),
          ],
          personsSheet: t('Persons'),
          // The names of the types as the admins have named them
          types: Object.fromEntries(
            contactTypes.map((type) => [type, typeOptions.labelOf(type)])
          ),
        },
        type: selectedType,
      });

      // The name the server gave, e.g. Yhteystiedot_2026-10-15.xlsx
      const disposition = response.headers['content-disposition'] ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = match ? decodeURIComponent(match[1]) : 'contacts.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(addMessage(t('export.Failed')));
    } finally {
      setExporting(false);
    }
  };

  const getTypes = () => {
    customerApi.getTypes().then((contactTypeData) => {
      setContactTypes(contactTypeData);
    });
  };

  const setFilter = (key: keyof IContactColumnFilters, value: string) =>
    setColumnFilters((current) => ({ ...current, [key]: value }));

  // A click on the search of a column does not open the contact
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  // The name of the column with its search below it
  const header =
    (control: JSX.Element) =>
    ({ text }: { text: string }): JSX.Element =>
      (
        <div className="contacts-column-header">
          <span>{text}</span>
          {control}
        </div>
      );

  const textSearch = (
    key: keyof IContactColumnFilters,
    type: 'date' | 'number' | 'search' = 'search'
  ) => (
    <Input
      aria-label={t('Search column')}
      bsSize="sm"
      min={type === 'number' ? 0 : undefined}
      onChange={(event) => setFilter(key, event.target.value)}
      onClick={stop}
      placeholder={type === 'search' ? t('Search column') : undefined}
      title={type === 'date' ? t('Credit checked from') : undefined}
      type={type}
      value={columnFilters[key] ?? ''}
    />
  );

  // The type, the company, its business id and address, how many persons it has and its
  // latest credit check with its rating
  const columns = [
    {
      dataField: 'contactTypes',
      formatter: (types: string[] | undefined) =>
        (types ?? []).map((type) => typeOptions.labelOf(type)).join(', '),
      headerFormatter: header(
        <Input
          aria-label={t('Contact Types')}
          bsSize="sm"
          onChange={(event) => setSelectedType(event.target.value)}
          onClick={stop}
          type="select"
          value={selectedType}
        >
          <option value="">{t('common:All')}</option>
          {typeOptions.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      ),
      text: t('Contact Type'),
    },
    {
      dataField: 'companyName',
      // The auxiliary business name after the name, e.g. "Yritys Oy (Apunimi)"; a locked
      // company with its lock
      formatter: (
        companyName: string,
        contact: { auxiliaryName?: null | string; locked?: boolean }
      ) =>
        `${
          contact.auxiliaryName
            ? `${companyName} (${contact.auxiliaryName})`
            : companyName
        }${contact.locked ? ' 🔒' : ''}`,
      headerFormatter: header(textSearch('companyName')),
      text: t('Company'),
    },
    {
      dataField: 'businessId',
      headerFormatter: header(textSearch('businessId')),
      text: t('Business Id'),
    },
    {
      dataField: 'streetAddress',
      headerFormatter: header(textSearch('streetAddress')),
      text: t('Street Address'),
    },
    {
      dataField: 'postalNumber',
      headerFormatter: header(textSearch('postalNumber')),
      text: t('Postal Number'),
    },
    {
      dataField: 'city',
      headerFormatter: header(textSearch('city')),
      text: t('Place'),
    },
    {
      dataField: 'contactPersonCount',
      headerFormatter: header(textSearch('personCount', 'number')),
      text: t('Person count'),
    },
    {
      dataField: 'creditCheckedAt',
      formatter: (checkedAt: null | string | undefined) =>
        checkedAt ? formatServerDate(checkedAt) : '',
      headerFormatter: header(textSearch('creditCheckedFrom', 'date')),
      text: t('credit.Checked at'),
    },
    {
      dataField: 'creditRating',
      headerFormatter: header(
        <Input
          aria-label={t('credit.Rating')}
          bsSize="sm"
          onChange={(event) => setFilter('creditRating', event.target.value)}
          onClick={stop}
          type="select"
          value={columnFilters.creditRating ?? ''}
        >
          <option value="">{t('common:All')}</option>
          {creditRatings.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      ),
      text: t('credit.Rating'),
    },
  ];

  // The widths of the columns follow their content
  const sized = withContentWidths(columns, contactData || []);

  const handleTableChange = (type: any, { page, sizePerPage }: any) => {
    setPage(page);
    setSizePerPage(sizePerPage);
    setOffset((page - 1) * sizePerPage);
  };

  const createContact = () => {
    history.push('/contacts/create');
  };

  useEffect(() => getTypes(), []);

  // The searches of the columns from the first page, once the user has stopped typing
  useEffect(() => {
    const timeout = setTimeout(() => {
      const sent = Object.fromEntries(
        Object.entries(columnFilters).filter(([, value]) => value !== '')
      );
      setAppliedFilters(sent);
      setPage(1);
      setOffset(0);
    }, FILTER_DELAY);
    return () => clearTimeout(timeout);
  }, [columnFilters]);

  useEffect(() => {
    const getContacts = async () => {
      try {
        if (
          (searchTerm !== '' && searchTerm !== prevSearchTerm) ||
          (selectedType !== '' && selectedType !== prevSelectedType)
        ) {
          setPage(1);
          setOffset(0);
        }
        setPrevSearchTerm(searchTerm);
        setPrevSelectedType(selectedType);
        const list = await customerApi.get(
          {
            limit: sizePerPage,
            offset,
            type: selectedType,
            search: searchTerm,
          },
          appliedFilters
        );
        const count = await customerApi.count(
          searchTerm,
          selectedType,
          appliedFilters
        );
        setContactData(list);
        setTotalSize(count.count);
      } catch (error) {
        // define action
      }
    };
    getContacts();
  }, [appliedFilters, offset, searchTerm, sizePerPage, selectedType]);

  useEffect(() => {
    if (selectedRow !== undefined && contactData.length > 0) {
      history.push(`/contacts/${contactData[selectedRow || 0].id}`);
    }
  }, [history, selectedRow, contactData]);

  return (
    <PageContainer className="contacts-page" fullWidth>
      <Row>
        <Col>
          <p className="header-text">{t('common:Contacts')}</p>
          {/* The companies, or the persons of all of them */}
          <div className="contacts-tabs" role="tablist">
            {(['companies', 'persons'] as const).map((name) => (
              <button
                aria-selected={tab === name}
                className={`contacts-tab ${tab === name ? 'active' : ''}`}
                key={name}
                onClick={() => setTab(name)}
                role="tab"
                type="button"
              >
                {t(`tabs.${name}`)}
              </button>
            ))}
          </div>
        </Col>

        <Col className="header-add-btn button-margin-top">
          <PrimaryButton
            className="contacts-export-button"
            disabled={exporting}
            onClick={exportExcel}
          >
            {(exporting
              ? t('export.Exporting')
              : t('export.Button')
            ).toLocaleUpperCase()}
          </PrimaryButton>
          {canWrite ? (
            <PrimaryButton onClick={() => createContact()}>
              {t('common:Add').toLocaleUpperCase()}
            </PrimaryButton>
          ) : null}
        </Col>
      </Row>
      {tab === 'persons' ? (
        <div className="basic-table clean-table content-width-table">
          <ContactPersonsTab />
        </div>
      ) : (
        <div className="basic-table clean-table content-width-table">
          <div className="search-type-row">
            <SearchInput setSearchTerms={setSearchTerm} />
          </div>
          <div
            className="table-scroll"
            style={
              { '--table-min-width': sized.minWidth } as React.CSSProperties
            }
          >
            <BasicTable
              columns={sized.columns}
              data={contactData || []}
              page={page}
              sizePerPage={sizePerPage}
              totalSize={totalSize}
              handleTableChange={handleTableChange}
              setSelectedRow={setSelectedRow}
              keyField="id"
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Contacts;
